import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import {
  FaPlay,
  FaPause,
  FaBackwardStep,
  FaForwardStep,
} from "react-icons/fa6";
import { LuX, LuVolume2 } from "react-icons/lu";
import { HiMusicalNote } from "react-icons/hi2";

export const Route = createFileRoute("/dev/_devLayout/global-player-test")({
  component: GlobalPlayerTest,
});

function GlobalPlayerTest() {
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [_duration, setDuration] = useState("0:00");
  const [remainingTime, setRemainingTime] = useState("-0:00");

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#1F232B",
      progressColor: "#43DDA7",
      height: 32,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      cursorWidth: 0,
      normalize: true,
      backend: "WebAudio",
    });

    wavesurferRef.current = wavesurfer;

    wavesurfer.load("/beat-of-the-music.mp3");

    wavesurfer.on("ready", () => {
      setIsLoading(false);
      const dur = wavesurfer.getDuration();
      setDuration(formatTime(dur));
      setRemainingTime(`-${formatTime(dur)}`);
    });

    wavesurfer.on("audioprocess", () => {
      const time = wavesurfer.getCurrentTime();
      const dur = wavesurfer.getDuration();
      setCurrentTime(formatTime(time));
      setRemainingTime(`-${formatTime(dur - time)}`);
    });

    wavesurfer.on("play", () => {
      setIsPlaying(true);
    });

    wavesurfer.on("pause", () => {
      setIsPlaying(false);
    });

    wavesurfer.on("finish", () => {
      setIsPlaying(false);
    });

    return () => {
      wavesurfer.destroy();
    };
  }, []);

  const handlePlayPause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-end">
      <div className="w-full">
        {/* Global Player Bar */}
        <div className="bg-black px-8 py-2 h-[60px] flex items-center gap-8 max-w-[1440px] mx-auto">
          {/* Transport Controls */}
          <div className="flex items-center gap-0.5">
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white/80 hover:text-white">
              <FaBackwardStep className="w-4 h-4" />
            </button>
            <button
              onClick={handlePlayPause}
              disabled={isLoading}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <FaPause className="w-5 h-5 text-[#111315]" />
              ) : (
                <FaPlay className="w-5 h-5 text-[#111315] ml-0.5" />
              )}
            </button>
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white/80 hover:text-white">
              <FaForwardStep className="w-4 h-4" />
            </button>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center shadow-lg">
              <HiMusicalNote className="w-5 h-5 text-white/30" />
            </div>
            <div className="max-w-[160px]">
              <p className="font-semibold text-white text-base truncate">
                beat of the music
              </p>
              <p className="text-xs text-[#A5ABB3] truncate">Creator Name</p>
            </div>
          </div>

          {/* Waveform + Times */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-sm font-semibold text-white">
              {currentTime}
            </span>
            <div ref={containerRef} className="flex-1 h-8" />
            <span className="text-sm font-semibold text-white">
              {remainingTime}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-0">
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white/80 hover:text-white">
              <LuVolume2 className="w-5 h-5" />
            </button>
            <button className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white/80 hover:text-white">
              <LuX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Test Info */}
        <div className="text-center py-4">
          <p className="text-gray-500 text-xs">
            Clean WaveSurfer.js implementation - Iterating on Figma design
          </p>
        </div>
      </div>
    </div>
  );
}
